import {
  BannerLite,
  EventLite,
  FeaturedCard,
  LargeCard,
  LargeDisplayItem,
  Section,
  SmallCard,
  SmallDisplayItem
} from '@flocasts/flosports30-types/dist/watch'
import { notNullOrUndefined } from '../../shared/functions/type-guards'
import { StreamListItemLiteWithLiveEventId, WatchListItemAnalytics } from './watch.interfaces'
import { SmallContentCardComponentProps } from '../../libs/content-card/small-content-card/small-content-card.component'
import { mapDisplayItemToSmallContentCard } from '../../libs/content-card/small-content-card/small-content-card.utility'
import { LargeEventCardComponentProps } from '../../libs/large-event-card/large-event-card.interfaces'
import { mapEventLiteToLargeEventCard } from '../../libs/large-event-card/large-event-card.utility'
import { mapDisplayItemToLargeOrFeaturedContentCard } from 'src/app/libs/content-card/content-card.utility'
import { LargeContentCardComponentProps } from 'src/app/libs/content-card/content-card.interface'
import { AdditionalOptions } from '../../shared/ads/ad/ad-component.interface'
import { environment } from '../../../environments/environment'
import { LiveEventStatus } from '@flocasts/flosports30-types/dist/entity'

/**
 * Return a boolean representing if a Section has items of type LargeCard.
 */
const isLargeCard = (section: Section): section is LargeCard => section.items_style === 'large-card'

/**
 * Filter an array of Sections down to only those containing LargeCards.
 */
export const filterToLargeCards = (sections: ReadonlyArray<Section>): ReadonlyArray<LargeCard> => {
  return sections.filter(isLargeCard)
}

/**
 * Filter an array of LargeCard sections down to only Live & Upcoming.
 *
 * As of August 2021, this will only ever be one section,
 * but this was written to support a case with multiple Live & Upcoming sections in the future.
 */
export const filterToLiveAndUpcoming = (largeCardSections: ReadonlyArray<LargeCard>): ReadonlyArray<LargeCard> => {
  return largeCardSections.filter(section => section.title === 'Live & Upcoming')
}

/**
 * Return a boolean representing if a LargeDisplayItem is of type EventLite.
 */
const isEventLite = (item: LargeDisplayItem): item is EventLite => item.type === 'event'

/**
 * Filter an array of LargeDisplayItems down to only those that are EventLites.
 */
export const filterToEventLites = (items: ReadonlyArray<LargeDisplayItem>): ReadonlyArray<EventLite> => {
  return items.filter(isEventLite)
}

/**
 * Filter an array of EventLite objects by the status (default 'LIVE') of their live_event object.
 */
export const filterEventLitesByStatus = (
  events: ReadonlyArray<EventLite>,
  status: LiveEventStatus = 'LIVE'
): ReadonlyArray<EventLite> => {
  return events.filter(event => event.live_event?.status === status)
}

/**
 * Map an array of EventLites down to each event's first active stream.
 */
export const mapEventLitesToActiveStreams = (
  events: ReadonlyArray<EventLite>
): ReadonlyArray<StreamListItemLiteWithLiveEventId> => {
  const activeStreams = events.map(event =>
    event.live_event?.stream_list
      .map(stream => ({ ...stream, liveEventId: event.live_event?.id }))
      .find(stream => stream.stream_active)
  )

  return activeStreams.filter(notNullOrUndefined)
}

/**
 * Map an array of LargeCards to an array of live event id's to check for geo-blocking.
 */
export const mapLargeCardsToPossibleGeoBlockedIds = (cards: ReadonlyArray<LargeCard>): ReadonlyArray<number> => {
  const idsToCheck = cards.map((section: LargeCard) => {
    return section.items
      .filter(isEventLite)
      .filter(item => item.check_geo_restriction && !!item.live_event)
      .map(item => item.live_event?.id)
      .filter(notNullOrUndefined)
  })

  // flatten the array of arrays of numbers down to one array of numbers
  return idsToCheck.reduce((a, b) => a.concat(b), [])
}

/**
 * Take LargeCard from the API, add analytics, and map to appropriate display component props.
 */
export const prepLargeCardsForDisplay = (
  section: LargeCard,
  sectionRowNumber: number,
  arrayLength: number,
  collectionName: string | null,
  userIsPremium: boolean
): ReadonlyArray<LargeEventCardComponentProps | LargeContentCardComponentProps> => {
  return section.items.map((item: LargeDisplayItem, itemIndex: number) => {
    const listItemAnalytics = getWatchListItemAnalytics(sectionRowNumber, itemIndex, arrayLength, collectionName)
    switch (item.type) {
      case 'event':
        return mapEventLiteToLargeEventCard(item, listItemAnalytics)
      case 'card':
      case 'collection':
      case 'video':
        return mapDisplayItemToLargeOrFeaturedContentCard(item, listItemAnalytics, userIsPremium)
    }
  })
}

/**
 * Take FeaturedCard from the API, add analytics, and map to appropriate display component props.
 * similar to prepLargeCardsForDisplay and prepSmallCardsForDisplay, except item.type will always be 'card'
 */
export const prepFeaturedCardsForDisplay = (
  section: FeaturedCard,
  sectionRowNumber: number,
  arrayLength: number,
  collectionName: string | null,
  userIsPremium: boolean
): ReadonlyArray<LargeContentCardComponentProps> => {
  return section.items.map((item: BannerLite, itemIndex: number) => {
    const listItemAnalytics = getWatchListItemAnalytics(sectionRowNumber, itemIndex, arrayLength, collectionName)
    return mapDisplayItemToLargeOrFeaturedContentCard(item, listItemAnalytics, userIsPremium)
  })
}

/**
 * Take SmallCard from the API, add analytics, and map to appropriate display component props.
 */
export const prepSmallCardsForDisplay = (
  section: SmallCard,
  sectionRowNumber: number,
  arrayLength: number,
  collectionName: string | null,
  userIsPremium: boolean
): ReadonlyArray<SmallContentCardComponentProps> => {
  return section.items.map((item: SmallDisplayItem, itemIndex: number) => {
    const listItemAnalytics = getWatchListItemAnalytics(sectionRowNumber, itemIndex, arrayLength, collectionName)
    switch (item.type) {
      case 'card':
      case 'collection':
      case 'video':
        return mapDisplayItemToSmallContentCard(item, listItemAnalytics, userIsPremium)
    }
  })
}

/**
 * All analytics properties related to list items within a section & row.
 *
 * @param rowNumber the row (i.e., section, scrolling vertically), the item exists
 * @param rowItemNumber the place within the section (scrolling horizontally) the item exists
 * @param arrayLength the number of items within the section of that item
 * @param collectionName the title of the section the item exists in
 */
export const getWatchListItemAnalytics = (
  rowNumber: number,
  rowItemNumber: number,
  arrayLength: number,
  collectionName: string | null
): WatchListItemAnalytics => {
  return {
    row_number: rowNumber,
    row_item_number: rowItemNumber,
    array_length: arrayLength,
    collection_name: collectionName
  }
}

/**
 * Default additional options for ads on watch.
 */
export const DEFAULT_ADDITIONAL_OPTS: AdditionalOptions = {
  deferClick: environment.advertising.preventClickTimeout
}
